// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { findStep } from "@keller/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { StepperContext } from "@keller/App/Widgets/CheckoutStepper/StepperContext";
import Check from "@keller/XDL/Icons/Check";
import { color, xScreen } from "@keller/XDL/Utilities";
import React, { useContext } from "react";
import { css } from "styled-components";

interface OwnProps {}

type Props = OwnProps;

export interface CheckoutStepperStyles {
    container?: InjectableCss;
    stepperWrapper?: InjectableCss;
    stepperItem: {
        wrapper?: InjectableCss;
        insideWrapper?: InjectableCss;
        circle?: InjectableCss;
        circleIcon?: IconPresentationProps;
        title?: TypographyProps;
    };
}

export const styles: CheckoutStepperStyles = {
    container: {
        css: css`
            max-width: 388px;
        `,
    },
    stepperWrapper: {
        css: css`
            display: flex;
            width: 100%;
            align-items: flex-start;
            justify-content: space-between;
            & > li:last-child:after {
                display: none;
            }
        `,
    },
    stepperItem: {
        wrapper: {
            css: css`
                display: flex;
                flex: 1 0 88px;
                justify-content: flex-start;
                position: relative;
                &:after {
                    content: "";
                    height: 1px;
                    width: 100%;
                    flex: 1;
                    background: ${color("common.ui6")};
                    top: 28px;
                    position: absolute;
                    transform: translateY(-14px);
                    left: 34px;
                }
                ${({ isCompleted }: any) =>
                    isCompleted &&
                    css`
                        &:after {
                            background-color: ${color("common.ui2")};
                        }
                    `}

                ${xScreen("lte", "xs")} {
                    flex: 1 0 auto;
                }
            `,
        },
        insideWrapper: {
            css: css`
                display: flex;
                z-index: 10;
                align-items: center;
                flex-direction: column;
            `,
        },
        circle: {
            css: css`
                display: flex;
                align-items: center;
                justify-content: center;
                width: 28px;
                height: 28px;
                border-radius: 50%;
                border: 1px solid ${color("common.ui6")};
                background-color: ${color("common.ui4")};

                // For step number
                font-size: 13px;
                font-weight: 900;
                letter-spacing: 0;

                ${({ isCompleted, isActive }: any) =>
                    (isCompleted || isActive) &&
                    css`
                        cursor: pointer;
                        border: 1px solid ${color("common.ui2")};
                        background-color: ${color("common.ui2")};
                        color: ${color("common.white")};
                    `}
            `,
        },
        circleIcon: {
            src: Check,
            size: 16,
            color: "common.white",
        },
        title: {
            size: 12,
            weight: "bold",
            css: css`
                margin-top: 8px;
                ${({ isCompleted, isActive }: any) =>
                    (isCompleted || isActive) &&
                    css`
                        cursor: pointer;
                    `}
            `,
        },
    },
};

export const CheckoutStepper: React.FC<Props> = props => {
    const { steps, currentStep, maxStep, moveToStepHandler } = useContext(StepperContext);
    const currentStepText = findStep(steps, currentStep).name;

    const StepperComponent = () => {
        const style = styles.stepperItem;
        return (
            <Typography as="ul" {...styles.stepperWrapper}>
                {steps.map((item, index) => {
                    const isCompleted = item.index < currentStep;
                    const isActive = item.index === currentStep;
                    const isClickable = item.index <= maxStep;

                    const props = { isCompleted, isActive, isClickable };
                    const className = `${isActive ? "active" : ""} ${isCompleted ? "completed" : ""} ${
                        isClickable ? "clickable" : ""
                    }`.trim();

                    return (
                        <Typography
                            as="li"
                            {...style.wrapper}
                            {...props}
                            key={item.name}
                            className={className}
                            onClick={() => isClickable && moveToStepHandler(item.index)}
                        >
                            <StyledWrapper {...style.insideWrapper} {...props}>
                                <Typography {...style.circle} {...props}>
                                    {isCompleted ? <Icon {...style.circleIcon} /> : index + 1}
                                </Typography>
                                <Typography {...style.title} {...props}>
                                    {item.name}
                                </Typography>
                            </StyledWrapper>
                        </Typography>
                    );
                })}
            </Typography>
        );
    };

    return (
        <StyledWrapper {...styles.container}>
            <VisuallyHidden aria-current="step">{translate(currentStepText)}</VisuallyHidden>
            <StepperComponent />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CheckoutStepper,
    definition: {
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
        displayName: "Checkout Stepper",
        fieldDefinitions: [],
    },
};

export default widgetModule;
